import React from 'react';
import { PortableText as SanityPortableText } from '@portabletext/react';
import Image from 'next/image';
import { urlForImage } from '../../app/studio/lib/image';
import { sanityLoader } from '../../../utils/sanityLoader';
import VideoEmbed from '../../../components/content/VideoEmbed';

// Custom components for rendering different Portable Text blocks
const components = {
  types: {
    image: ({ value }) => {
      if (!value?.asset?._ref) {
        return null;
      }
      
      // Handle different display styles
      const getWidthClass = (displayStyle) => {
        switch (displayStyle) {
          case 'full-width':
            return 'w-full mx-auto';
          case 'small':
            return 'w-full md:w-1/2 mx-auto';
          case 'body-width':
          default:
            return 'w-full mx-auto';
        }
      };
      
      const widthClass = getWidthClass(value.displayStyle);
      
      const imageUrl = urlForImage(value);
      
      // Only render if we have a valid image URL
      if (!imageUrl) {
        console.warn('Invalid image in Portable Text:', value);
        return null;
      }
      
      return (
        <div className={`relative ${widthClass} my-8 rounded-lg overflow-hidden`}>
          <div className="relative w-full h-64 md:h-96">
            <Image
              src={imageUrl}
              alt={value.alt || 'Image'}
              fill
              loader={sanityLoader}
              sizes="(max-width: 768px) 100vw, 800px"
              className="object-cover"
            />
          </div>
          {value.caption && (
            <div className="mt-2 text-sm text-gray-600 italic text-center">
              {value.caption}
            </div>
          )}
        </div>
      );
    },
    video: ({ value }) => <VideoEmbed value={value} />,
  },
  marks: {
    link: ({ children, value }) => {
      const rel = !value.href.startsWith('/') ? 'noreferrer noopener' : undefined;
      const target = !value.href.startsWith('/') ? '_blank' : undefined;
      return (
        <a 
          href={value.href} 
          rel={rel}
          target={target}
          className="text-accent-color hover:underline"
        >
          {children}
        </a>
      );
    },
    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    underline: ({ children }) => <span className="underline">{children}</span>,
    code: ({ children }) => <code className="bg-gray-100 rounded px-1 py-0.5 font-mono text-sm">{children}</code>,
    fontStyle: ({ children, value }) => {
      const fontFamily = value?.font || 'default';
      let fontClass = '';
      
      switch(fontFamily) {
        case 'neuropol':
          fontClass = 'font-neuropol';
          break;
        case 'sans-serif':
          fontClass = 'font-sans';
          break;
        case 'serif':
          fontClass = 'font-serif';
          break;
        case 'monospace':
          fontClass = 'font-mono';
          break;
        case 'cursive':
          fontClass = 'font-cursive';
          break;
        default:
          fontClass = '';
      }
      
      return <span className={fontClass}>{children}</span>;
    },
    neuropol: ({ children }) => <span className="font-neuropol">{children}</span>,
  },
  block: {
    h1: ({ children, value }) => {
      const fontStyle = value?.markDefs?.find(mark => mark._type === 'fontStyle')?.fontFamily || '';
      return <h1 className={`text-4xl md:text-5xl font-bold my-8 ${fontStyle}`}>{children}</h1>;
    },
    h2: ({ children, value }) => {
      const fontStyle = value?.markDefs?.find(mark => mark._type === 'fontStyle')?.fontFamily || '';
      return <h2 className={`text-3xl md:text-4xl font-bold my-6 ${fontStyle}`}>{children}</h2>;
    },
    h3: ({ children, value }) => {
      const fontStyle = value?.markDefs?.find(mark => mark._type === 'fontStyle')?.fontFamily || '';
      return <h3 className={`text-xl md:text-2xl font-bold my-4 ${fontStyle}`}>{children}</h3>;
    },
    h4: ({ children, value }) => {
      const fontStyle = value?.markDefs?.find(mark => mark._type === 'fontStyle')?.fontFamily || '';
      return <h4 className={`text-lg md:text-xl font-bold my-3 ${fontStyle}`}>{children}</h4>;
    },
    normal: ({ children }) => <p className="text-base mb-4">{children}</p>,
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-accent-color pl-4 my-6 italic">{children}</blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => <ul className="list-disc pl-6 mb-6">{children}</ul>,
    number: ({ children }) => <ol className="list-decimal pl-6 mb-6">{children}</ol>,
  },
  listItem: {
    bullet: ({ children }) => <li className="mb-1">{children}</li>,
    number: ({ children }) => <li className="mb-1">{children}</li>,
  },
};

export default function PortableText({ value }) {
  return <SanityPortableText value={value} components={components} />;
} 